import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import styles from "./tenantlist.module.css";
import { Link, useHistory } from "react-router-dom";
import { Home, Search, Calendar, Plus, X, ArrowRight, Filter, ArrowLeft, MoreVertical, Send, Award, Printer } from "react-feather";
import Helmet from "react-helmet";
import { TableLoader } from "../../components/LoaderC";
import Footer from "../../components/Footer";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import AddForm from "./components/AddForm";
import { Typography, Snackbar, Slide, IconButton, Dialog, DialogTitle, DialogContent, TablePagination, DialogContentText, DialogActions, Button, Tooltip } from "@mui/material";
import DateRangePicker from "rsuite/DateRangePicker";
import PeoplePageTitle from "./components/PeoplePageTitle";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-smart-toaster";
import { useDispatch, useSelector } from "react-redux";
import { communicationVoiceCallScreenOpen } from "../../redux/actions/communicationVoiceCallScreenAction";
import { setVoiceCallInfo } from "../../redux/actions/communicationVoiceCallInfo";
import DemandLetterForm from "./components/DemandLetterForm";
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";
import FilterAutocomplete from "../../components/FilterAutocomplete";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const SEARCH_DEBOUNCE_MS = 400;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const StatusFilterList = [
  { value: "all", label: "All" },
  { value: "Active", label: "Active" },
  { value: "Archived", label: "Archived" },
  { value: "Moved Out", label: "Moved Out" },
  { value: "Payment Due", label: "Payment Due" },
];

const TenantList = () => {
  const voiceCallInfo = useSelector((store) => store.communicationVoiceCallInfoReducer);
  const redirect = useHistory();
  const dispatch = useDispatch();
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [contentLoading, setContentLoading] = useState(true);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refetching, setRefetching] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [propertyList, setPropertyList] = useState([]);
  const [filterData, setFilterData] = useState({ searchKey: "", view: "", property: "", created: "" });
  const [page, setPage] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [perPage, setPerPage] = useState(8);
  const [renewalModalOpen, setRenewalModalOpen] = useState(false);
  const [renewalLinkId, setRenewalLinkId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openLeaderBoardModal, setOpenLeaderBoardModal] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [openCallModal, setOpenCallModal] = useState(false);
  const [prospectInfoObj, setProspectInfoObj] = useState(null);
  const [openDemandLetterModal, setOpenDemandLetterModal] = useState(false);

  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const lastQueryKeyRef = useRef(null);

  useEffect(() => {
    const getPropertyList = async () => {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        const response = await axios(process.env.REACT_APP_APPLICANT_API_URL + "v1/get-all-properties", {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });
        setPropertyList([{ value: "all", label: "Any", apartments: [] }].concat(response.data?.data || []));

        if (localStorage.getItem("propertyFilterData")) {
          let filterPropertyId = localStorage.getItem("propertyFilterData");
          localStorage.removeItem("propertyFilterData");

          setFilterData((prev) => {
            return { ...prev, property: parseInt(filterPropertyId) };
          });
        }
      } catch (error) {
        console.error("[Tenants] Failed to load property list", error);
        toast.error(error?.response?.data?.message || "Unable to load properties. Please try again.");
      }
    };

    getPropertyList();
  }, [getAccessTokenSilently]);

  // Search is sent after typing pauses; property, status and date range apply immediately
  const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
  const hasCreatedRange = Boolean(filterData.created?.[0] && filterData.created?.[1]);
  const createdStart = hasCreatedRange ? moment(filterData.created[0]).format("YYYY-MM-DD") : "";
  const createdEnd = hasCreatedRange ? moment(filterData.created[1]).format("YYYY-MM-DD") : "";
  const query = useMemo(() => ({
    search: debouncedSearch.trim(),
    property: filterData.property,
    status: filterData.view,
    start: createdStart,
    end: createdEnd,
  }), [debouncedSearch, filterData.property, filterData.view, createdStart, createdEnd]);

  const fetchTenants = useCallback(async () => {
    const seq = ++requestSeq.current;

    const isFirstLoad = !hasLoadedOnceRef.current;
    if (isFirstLoad) setContentLoading(true);
    else setRefetching(true);

    let params = { page: page + 1, per_page: perPage };
    if (query.search !== "") params.search = query.search;
    if (isActiveFilter(query.property)) params.property_id = query.property;
    if (isActiveFilter(query.status)) params.status = query.status;
    if (query.start && query.end) {
      params.start = query.start;
      params.end = query.end;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/tenants/grid", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const rows = response.data?.data || [];
      const meta = response.data?.meta || {};
      const total = meta?.total ?? 0;

      // The current page can become empty when the data changes; move back to the last page that has data
      if (rows.length === 0 && page > 0 && total > 0) {
        setPage(Math.max(0, (meta?.last_page ?? 1) - 1));
        return;
      }

      setFilteredData(rows);
      setTotalCount(total);
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Tenants] Failed to load tenant list", error);
      toast.error(error?.response?.data?.message || "Unable to load tenants. Please try again.");

      setLoadError(true);
      setFilteredData([]);
      setTotalCount(0);
    } finally {
      if (seq === requestSeq.current) {
        if (isFirstLoad) setContentLoading(false);
        else setRefetching(false);
      }
    }
  }, [getAccessTokenSilently, query, page, perPage]);

  // Load the page when paging, search, filters or data change. A changed query first
  // goes back to page 1, which then triggers a single request.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    const queryChanged = lastQueryKeyRef.current !== null && lastQueryKeyRef.current !== queryKey;
    lastQueryKeyRef.current = queryKey;
    if (queryChanged && page !== 0) {
      setPage(0);
      return;
    }

    fetchTenants();
  }, [queryKey, fetchTenants, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The list is paginated on the server, so reload it after adding a tenant
  const addTenant = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const sendLeaseRenewalLink = async () => {
    setRenewalModalOpen(false);

    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
    axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/send-lease-renewal-email/" + renewalLinkId, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
    }).catch((error) => {
      setLoading(false);
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      toast.error("Error occured. Try Again!");
    });
  };

  const addToLeaderBoard = async () => {
    setOpenLeaderBoardModal(false);

    setLoading(true);

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
    return axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/add-to-leaderboard/" + tenantId, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg(response.data.message);
      setSnackbarOpen(true);

      setFilteredData((prevData) =>
        prevData.map((tenant) => tenant.id === tenantId ? { ...tenant, is_leaderboard: 1 } : tenant)
      );
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const getDemandLetter = async (data) => {
    setOpenDemandLetterModal(false);
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/demand-letter/" + tenantId, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: data
    }).then((response) => {
      setLoading(false);

      window.open(response.data.url, "_blank");

      setSnackbarOpen(true);
      setSnackbarMsg("Demand report has been generated successfully.");
    }).catch((error) => {
      setLoading(false);
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
    });
  }

  const initiateVoiceCall = (item) => {
    if (voiceCallInfo) {
      toast.error("You are on anotehr call.");
    } else {
      setOpenCallModal(true);
      setProspectInfoObj(item);
    }
  };
  const initiateVoiceCallConfirm = () => {
    setOpenCallModal(false);
    let newProspectInfo = {
      id: prospectInfoObj?.id,
      full_name: prospectInfoObj?.first_tenant?.full_name,
      initial: prospectInfoObj?.first_tenant?.initial,
      phone: prospectInfoObj?.first_tenant?.phone,
      user_avatar: prospectInfoObj?.first_tenant?.user_image,
      type: "Tenant"
    };

    dispatch(communicationVoiceCallScreenOpen(true));
    dispatch(setVoiceCallInfo(newProspectInfo));
  };

  return (<>
    <Helmet title="Tenants" />
    {loading && <Loader />}

    <div className={`${styles.pageTitleWrap}`}>
      <PeoplePageTitle defaultPage={"Tenants"} />
      <Typography display="inline" className={`${styles.pageTitle} ${styles.Big}`}>Tenants</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>People</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Tenants</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          <div className="control-pane">
            <div className={`${styles.Row}`}>
              <div className={`${styles.SearchFilterSec}`}>
                <div className={`${styles.SearchCon}`}>
                  <div className={`${styles.SearchConSec}`}>
                    <Search />
                    <input type="text" placeholder="Search" value={filterData.searchKey} onChange={(e) => {
                      setFilterData((prev) => { return { ...prev, searchKey: e.target.value }; });
                    }} />
                  </div>
                </div>

                <div className={`${styles.FilterSec}`}>
                  <div className={filterOpen === true ? `${styles.FilterFieldSec} ${styles.Open}` : `${styles.FilterFieldSec}`}>
                    <p className={`${styles.FilterTitle}`}>
                      Filters
                      <span onClick={(e) => setFilterOpen(false)}><ArrowRight /></span>
                    </p>
                    <div className={`${styles.FilterCard} ${styles.Select} CusSelectFilter Filter Autocomplete`}>
                      <FilterAutocomplete
                        label="Property"
                        options={propertyList}
                        value={filterData?.property}
                        onChange={(value) => setFilterData((prev) => ({ ...prev, property: value }))}
                      />
                    </div>
                    <div className={`${styles.FilterCard} ${styles.Select} CusSelectFilter Filter Autocomplete`}>
                      <FilterAutocomplete
                        label="Status"
                        options={StatusFilterList}
                        value={filterData?.view}
                        onChange={(value) => setFilterData((prev) => ({ ...prev, view: value }))}
                      />
                    </div>
                    <div className={`${styles.FilterCard} Filter`}>
                      <div className={`${styles.DashFilterSec} DashFilterSec`}>
                        <DateRangePicker
                          format={"M/d/yy"}
                          placeholder={"Filter by date"}
                          placement={"bottomEnd"}
                          caretAs={Calendar}
                          showOneCalendar
                          value={filterData.created || null}
                          onChange={(e) =>
                            setFilterData((prev) => {
                              return { ...prev, created: e };
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <button className={`${styles.FilterBu}`} onClick={(e) => { setFilterOpen(true); }}><Filter /></button>
                  {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("tenant:create") > -1 && <button className={`${styles.AddNoteBu}`} onClick={(e) => { setOpenAddForm(true); }}>
                    <Plus /> <span>Tenant</span>
                  </button>}
                </div>
              </div>

              {contentLoading && (<div className="Loader TableLoader">
                <TableLoader />
              </div>)}

              {!contentLoading && (<div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>
                {filteredData.map((item, index) => {
                  return (<Link className={`${styles.ProspectCard}`} key={item?.id + "-" + index} to={`/people/tenants/details/${item.id}`}>
                    <div className={`${styles.ProspectCardUnder}`}>
                      <div className={`${styles.ProspectCardHeadSection}`}>
                        <div className={`${styles.ProsIMGSec}`}>
                          {item?.first_tenant?.user_image !== "" ? (<img src={item?.first_tenant?.user_image} className={`${styles.ProspectCardImg}`} alt="" />) : (<p>{item?.first_tenant?.initial}</p>)}
                        </div>

                        <div className={`${styles.HeadConSec}`}>
                          <div className={`${styles.prospectNameActionSec}`}>
                            <p className={`${styles.ProsUserName} ${styles.LimitName}`}>
                              {item?.first_tenant?.full_name}
                              {item?.first_tenant?.verification_status === 1 && (<img src="/static/img/verified.svg" className={`${styles.Varified}`} alt="" />)}
                            </p>
                            {item?.is_move_out === 0 && item?.is_archived === 1 && (<span className={`${styles.ArchivedBadge}`}>Archived</span>)}
                            {item?.is_move_out === 1 && (<span className={`${styles.ExpiredBadge}`}>Moved Out</span>)}
                            {item?.is_move_out === 0 && (<Dropdown className={`${styles.CardActionBU}`} onClick={(e) => { e.preventDefault(); }}>
                              <Dropdown.Toggle className={`${styles.ActionsBU} ProsDrBU`} id="dropdown-basic">
                                <MoreVertical />
                              </Dropdown.Toggle>

                              <Dropdown.Menu className={`${styles.ActionsBUList} ActionsBUList`}>
                                <Dropdown.Item className={`${styles.ActionsBUListItem}`} onClick={(e) => { setRenewalModalOpen(true); setRenewalLinkId(item?.id); }}>
                                  <Send />Send Renewal Email
                                </Dropdown.Item>

                                {!item?.is_leaderboard && (<Dropdown.Item className={`${styles.ActionsBUListItem}`} onClick={(e) => { setOpenLeaderBoardModal(true); setTenantId(item?.id); }}>
                                  <Award /> Add to Leaderboard
                                </Dropdown.Item>)}

                                <Dropdown.Item className={`${styles.ActionsBUListItem}`} onClick={(e) => { setOpenDemandLetterModal(true); setTenantId(item?.id); }}>
                                  <Printer /> Print Demand Letter
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>)}
                          </div>
                          <div className={`${styles.prospectNameActionSec}`}>
                            <Tooltip title="Start a voice call">
                              <p className={`${styles.ProsUserPhone}  ${styles.CallLink}`} onClick={(e) => { e.preventDefault(); initiateVoiceCall(item); }}>{item?.first_tenant?.phone}</p>
                            </Tooltip>
                            {item?.is_move_out === 0 && item?.leases?.lease_status === 2 && (<span className={`${styles.ExpireSoonBadge}`}>Expire Soon</span>)}
                            {item?.is_move_out === 0 && item?.leases?.lease_status === 1 && (<span className={`${styles.ExpiredBadge}`}>Expired</span>)}
                          </div>
                        </div>
                      </div>

                      <div className={`${styles.ProspectCardBodySec}`}>
                        <ul>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/bank-icon.svg" alt="" />Balance :</p>
                            <p className={`${styles.Content}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); localStorage.setItem("propertyFilterData", item?.property_id); localStorage.setItem("apartmentFilterData", item?.apt_id); redirect.push("/income/all-income"); }}>
                              {item.balance < 0 ? (<span className={`${styles.InfoStatusBadge} ${styles.Resolved}`}>{item?.balancestr} Over Paid</span>) : item.balance === 0 ? (<span className={`${styles.InfoStatusBadge} ${styles.Resolved}`}>{item?.balancestr} Balance</span>) : (<span className={`${styles.InfoStatusBadge} ${styles.Ongoing}`}>{item?.balancestr} Due</span>)}
                            </p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/bill-icon.svg" alt="" />Monthly Rent :</p>
                            <p className={`${styles.Content} ${styles.Hover}`}>{item?.formatted_rent}</p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/RequestM-Black.svg" alt="" />Maintenance :</p>
                            <p className={`${styles.Content}`}>
                              {item?.requests?.total === 0 && "-"}
                              {item?.requests?.total > 0 && item?.requests?.open > 0 && (<span className={`${styles.InfoStatusBadge} ${styles.Open}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); localStorage.setItem("propertyFilterData", item?.property_id); localStorage.setItem("apartmentFilterData", item?.apt_id); localStorage.setItem("statusFilterData", "Open"); redirect.push("/maintenance/request"); }}>
                                {item?.requests?.open} Open
                              </span>)}
                              {item?.requests?.total > 0 && item?.requests?.ongoing > 0 && (<span className={`${styles.InfoStatusBadge} ${styles.Ongoing}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); localStorage.setItem("propertyFilterData", item?.property_id); localStorage.setItem("apartmentFilterData", item?.apt_id); localStorage.setItem("statusFilterData", "In-Progress"); redirect.push("/maintenance/request"); }}>
                                {item?.requests?.ongoing} Ongoing
                              </span>)}
                            </p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/offline-deviceB.svg" alt="" />Offline Devices :</p>
                            <p className={`${styles.Content}`}>
                              {item?.devices?.offline === 0 ? ("-") : (<span className={`${styles.InfoStatusBadge} ${styles.Cancel}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); localStorage.setItem("propertyFilterData", item?.property_id); localStorage.setItem("aptFilterData", item?.apt_id); localStorage.setItem("statusFilterData", 0); redirect.push("/smart-devices/list"); }}>
                                {item?.devices?.offline} Offline Devices
                              </span>)}
                            </p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/LeaseIconB.svg" alt="" />Lease Term :</p>
                            <p className={`${styles.Content} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/leases/details/" + item?.id); }}>
                              {moment(item?.leases?.start_date).format("MM/DD/YYYY")}{" "}-{" "}{moment(item?.leases?.end_date).format("MM/DD/YYYY")}
                            </p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/home.svg" alt="" />Property :</p>
                            <p className={`${styles.Content} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>
                              {item?.property?.name}
                            </p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}><img src="/static/img/rooms.svg" alt="" />Unit :</p>
                            <p className={`${styles.Content}`}>
                              {item?.apartment?.apt_no}
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Link>);
                })}

                {filteredData.length > 0 && (<div className="Pagination TablePaginationNew List">
                  <TablePagination
                    count={totalCount}
                    color="primary"
                    page={page}
                    rowsPerPage={perPage}
                    onPageChange={(e, v) => setPage(v)}
                    rowsPerPageOptions={[
                      { label: "4", value: 4 },
                      { label: "8", value: 8 },
                      { label: "16", value: 16 },
                      { label: "50", value: 50 },
                    ]}
                    onRowsPerPageChange={(e) => {
                      setPage(0);
                      setPerPage(e.target.value);
                    }}
                    labelRowsPerPage={"Tenants per Page:"}
                  />
                </div>)}

                {loadError && (<div className={`${styles.NoDataMain}`}>
                  <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                  <p className={`${styles.NoDataTextSub}`}>we couldn't load the tenants.</p>
                  <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchTenants()}>Retry</button>
                </div>)}

                {!loadError && filteredData.length === 0 && (<div className={`${styles.NoDataMain}`}>
                  <div className={`${styles.NoDataIMG}`}>
                    <img src="/static/img/no-tenant.png" alt="No Data" />
                  </div>
                  <p className={`${styles.NoDataText}`}>Unfortunately!</p>
                  <p className={`${styles.NoDataTextSub}`}>we couldn't find a tenant at this time.</p>
                </div>)}
              </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      open={openAddForm}
      onClose={(e) => { setOpenAddForm(false); }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm CustomWidthPops"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setOpenAddForm(false); }}><ArrowLeft /></button>
        Add Tenant
        <button onClick={(e) => { setOpenAddForm(false); }}><X /></button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <div className={`${styles.AddTenant}`}>
          <AddForm closeAddForm={setOpenAddForm.bind(this)} addTenant={addTenant.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setOpenAddForm={setOpenAddForm.bind(this)} />
        </div>
      </DialogContent>
    </Dialog>

    <Dialog
      open={renewalModalOpen}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setRenewalModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to send lease renewal link?
        <button onClick={(e) => setRenewalModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">Send lease renewal link to the tenant via email? This will happen immediately and cannot be reversed.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => sendLeaseRenewalLink(e)} className="CancelPopupBU">Confirm</Button>
        <Button onClick={(e) => setRenewalModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={openLeaderBoardModal}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setOpenLeaderBoardModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to add to leader board?
        <button onClick={(e) => setOpenLeaderBoardModal(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">The Tenant Record will be added to leader board immediately. You can remove from leader board it at any time. </label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => setOpenLeaderBoardModal(false)} className="CancelPopupBU">Cancel</Button>
        <Button onClick={(e) => addToLeaderBoard(e)} className="SubmitPopupBU">Confirm</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={openCallModal}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setOpenCallModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Tenant Call Confirmation
        <button onClick={(e) => setOpenCallModal(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">Please confirm you wish to initiate a call to this tenant.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => setOpenCallModal(false)} className="CancelPopupBU">Cancel</Button>
        <Button onClick={(e) => initiateVoiceCallConfirm()} className="SubmitPopupBU">Proceed</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={openDemandLetterModal}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setOpenDemandLetterModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DemandLetterForm getDemandLetter={getDemandLetter.bind(this)} setOpenDemandLetterModal={setOpenDemandLetterModal.bind(this)} />
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={<IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={(e) => setSnackbarOpen(false)}
      >
        <X />
      </IconButton>}
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(TenantList, {
  onRedirecting: () => <Loader />,
});